import z from "zod";

import { controlSchema } from "@/shared/schemas/control";

export const imagingSchema = z.object({
  accession_number: z.string(),
  body_part: z.string(),
  control: controlSchema,
  description: z.string(),
  file: z.array(z.any()), // Puede contener strings (URLs) u objetos de informe
  institution_id: z.string(),
  institution_name: z.string(),
  iuid: z.string(),
  medical_report: z.array(z.any()), // Puede contener strings u objetos RIS
  modality: z.string(),
  number_imagings: z.number(),
  operation_id: z.string(),
  path: z.string(),
  patient_gender: z.string(),
  patient_id: z.string(),
  patient_name: z.string(),
  read: z.array(z.string()),
  referring_physician: z.string(),
  relevant: z.array(z.string()),
  size: z.number(),
  thumbnails: z.array(z.string()).optional().or(z.string().optional()),
  uid: z.string(),
  url_thubnail: z.string(),
  datetime: z.date(),
});

export type Imaging = z.infer<typeof imagingSchema>;

export const imagingsFiltersSchema = z.object({
  institution_id: z.string().optional(),
  patient_id: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  accession_number: z.string().optional(),
});

export type ImagingsFilters = z.infer<typeof imagingsFiltersSchema>;

export const imagingStudyNotificationSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("email"),
    days: z.number().min(1).max(365),
    study: z.string(),
    uid: z.string(),
    institution_id: z.string(),
    patient_name: z.string().optional(),
    phone: z.e164().optional(),
    email: z.email(),
    institution_name: z.string(),
    study_description: z.string(),
    modality: z.string(),
    logo: z.string().optional(),
    email_center: z.string().optional(),
    phone_center: z.string().optional(),
    phoneCenter: z.string().optional(),
    institution_email: z.string().optional(),
  }),
  z.object({
    type: z.literal("ws"),
    days: z.number().min(1).max(365),
    study: z.string(),
    uid: z.string(),
    institution_id: z.string(),
    patient_name: z.string().optional(),
    email: z.email().optional(),
    phone: z.e164(),
    institution_name: z.string(),
    study_description: z.string(),
    modality: z.string(),
    logo: z.string().optional(),
    email_center: z.string().optional(),
    phone_center: z.string().optional(),
    phoneCenter: z.string().optional(),
    institution_email: z.string().optional(),
  }),
]);

export type ImagingStudyNotification = z.infer<typeof imagingStudyNotificationSchema>;

export const imagingNotificationResponseSchema = z.object({
  status: z.string(),
  message: z.string(),
  token: z.string(),
  shortcut_id: z.string().optional(),
  tracking_id: z.string().optional(),
});

export type ImagingNotificationResponse = z.infer<
  typeof imagingNotificationResponseSchema
>;

export const imagingTrackingNotificationSchema = z.object({
  id_tracking: z.string(),
  created_at: z.date(),
  imaging: z.string(),
  object_shared: z.enum(["imaging_access", "report_access"]),
  status: z.enum(["sended_api", "delivered", "failed"]),
  to: z.string(),
  type: z.enum(["ws", "email"]),
  manual: z.boolean(),
  sender: z.string(),
  sct: z.string(),
});

export type ImagingTrackingNotification = z.infer<
  typeof imagingTrackingNotificationSchema
>;

// Schema for shared record
export const sharedRecordSchema = z.object({
  id: z.string(),
  imaging: z.string(),
  user_receiving: z.string(),
  user_sharing: z.string(),
  date_shared: z.string(),
  time_expired: z.string(),
  created_at: z.string(),
  phone: z.string(),
  status: z.enum(["active", "expired", "viewed"]).optional(),
  object_shared: z.enum(["imaging_access", "report_access"]).optional(),
  type: z.enum(["ws", "email"]).optional(),
  manual: z.boolean().optional(),
});

export type SharedRecord = z.infer<typeof sharedRecordSchema>;

export const sharedStudyRecordSchema = z.object({
  created_at: z.date(),
  imaging: z.string(),
  phone: z.array(z.string()),
  updated_at: z.date(),
  user_receiving: z.string(),
  user_sharing: z.string(),
});

export type SharedStudyRecord = z.infer<typeof sharedStudyRecordSchema>;

// Schema for shared records response
export const sharedRecordsResponseSchema = z.object({
  records: z.array(sharedRecordSchema),
  trackingNotifications: z.array(imagingTrackingNotificationSchema),
  hasNextPage: z.boolean(),
  totalCount: z.number(),
  lastDoc: z.any().optional(), // DocumentSnapshot from Firestore
});

export type SharedRecordsResponse = z.infer<typeof sharedRecordsResponseSchema>;

// Schema for shared stats - ahora basado en tracking
export const sharedStatsSchema = z.object({
  sharedStudies: z.number(), // Mantenido para compatibilidad
  trackingNotifications: z.number(), // Fuente principal de datos
});

export type SharedStats = z.infer<typeof sharedStatsSchema>;

export const imagingShortcutSchema = z.object({
  object: z.string(),
  url: z.string(),
  expired_at: z.date(),
  created_at: z.date(),
  user: z.string(),
  receiver: z.string(),
  active: z.boolean(),
  durationToken: z.number(),
});

export type ImagingShortcut = z.infer<typeof imagingShortcutSchema>;

export const imagingNotificationQueueSchema = z.object({
  created_at: z.date(),
  imaging: z.string(),
  link: z.string(),
  phone: z.string(),
  status: z.enum(["pending", "sent", "failed"]),
  type: z.string(),
});

export type ImagingNotificationQueue = z.infer<typeof imagingNotificationQueueSchema>;

// Esquemas para informes médicos
export const medicalReportSchema = z.object({
  id: z.string(),
  type: z.literal("ris"),
  accession_number: z.string(),
  institution: z.string(),
  patient_name: z.string(),
  patient_id: z.string(),
  study_date: z.date(),
  modality: z.string(),
  body_part: z.string(),
  report_text: z.string(),
  radiologist: z.string(),
  created_at: z.date(),
  updated_at: z.date(),
  status: z.enum(["draft", "completed", "verified", "amended"]),
});

export type MedicalReportType = z.infer<typeof medicalReportSchema>;

export const manualReportSchema = z.object({
  id: z.string(),
  type: z.literal("manual"),
  filename: z.string(),
  url: z.string(),
  size: z.number(),
  imaging_id: z.string(),
  patient_name: z.string(),
  patient_id: z.string(),
  modality: z.string(),
  body_part: z.string(),
  created_at: z.date(),
});

export type ManualReportType = z.infer<typeof manualReportSchema>;

export const reportSchema = z.discriminatedUnion("type", [
  medicalReportSchema,
  manualReportSchema,
]);

export type ReportType = z.infer<typeof reportSchema>;

export const shareReportSchema = z.discriminatedUnion("method", [
  z.object({
    method: z.literal("email"),
    reportId: z.string(),
    recipient: z.email(),
    institutionName: z.string(),
    patientName: z.string(),
    studyDescription: z.string(),
    logo: z.string().optional(),
  }),
  z.object({
    method: z.literal("ws"),
    reportId: z.string(),
    recipient: z.string().regex(/^\+?[1-9]\d{1,14}$/, "Número de teléfono inválido"),
    institutionName: z.string(),
    patientName: z.string(),
    studyDescription: z.string(),
    logo: z.string().optional(),
  }),
]);

export type ShareReportType = z.infer<typeof shareReportSchema>;

// Esquemas para el imagingService
export const generateExcelSchema = z.object({
  institution_id: z.string(),
  date_init: z.string(),
  date_end: z.string(),
});

export type GenerateExcelProps = z.infer<typeof generateExcelSchema>;

export const downloadImagingSchema = z.object({
  imaging_id: z.string(),
  filename: z.string().optional(),
});

export type DownloadImagingProps = z.infer<typeof downloadImagingSchema>;

export const saveSharedRecordSchema = z.object({
  imaging: z.string(),
  user_receiving: z.string(),
  user_sharing: z.string(),
  date_shared: z.string(),
  time_expired: z.string(),
  phone: z.string(),
});

export type SaveSharedRecordProps = z.infer<typeof saveSharedRecordSchema>;

export interface TemporaryAccessResult {
  created_at: Date;
  expiration_date: Date;
  resource_id: string;
  shared_resource: string;
  to: string;
  user_shared: string;
}

export interface NotificationApiPayload {
  userId: string;
  objectSharedId: string;
  /** Id Firestore del estudio (`imagings` doc id), no el iuid DICOM. */
  imaging?: string;
  resource_id?: string;
  nameTemplate: string;
  typeService: "email" | "whatsapp" | string;
  language: string;
  recipient: {
    email?: string;
    phone?: string;
  };
  trigger: string;
  description: string;
  manual: boolean;
}
